import React from 'react';
import { HiArrowPath } from 'react-icons/hi2';
import {
  TraverseIcon,
  TraverseTyreReferenceIcon,
  TraverseTyreComparisonIcon,
  TraverseIndoorsIcon,
  TraverseCatesbyIcon,
} from '../icons/AnalysisTypeIcons';
import { VelodromeIndoorIcon, VelodromeOutdoorIcon } from '../icons/VelodromeIcons';
import { SessionAnalysisType, SessionCatalog } from '../types/SessionAnalysisType';

interface SessionIconProps {
  analysisType: SessionAnalysisType | null;
  size?: number;
  className?: string;
}

/**
 * Get the session type name from SessionCatalog
 */
const getSessionTypeName = (analysisType: SessionAnalysisType | null): string => {
  if (analysisType === null) {
    return 'Unknown Session Type';
  }

  const catalogEntry = SessionCatalog[analysisType as keyof typeof SessionCatalog];
  if (catalogEntry && Array.isArray(catalogEntry) && catalogEntry.length > 0) {
    return catalogEntry[0] as string;
  }

  return 'Unknown Session Type';
};

/**
 * Returns the appropriate icon component for a given session analysis type.
 * - Traverse (and variants, null) → TraverseIcon (stretched 50% horizontally)
 * - Freeride → Loop arrow (HiArrowPath)
 * - Velodrome Indoors → VelodromeIndoorIcon
 * - Velodrome Outdoors → VelodromeOutdoorIcon
 */
export const SessionIcon: React.FC<SessionIconProps> = ({ analysisType, size = 24, className = '' }) => {
  const title = getSessionTypeName(analysisType);

  // Traverse Tyre Reference - stretched horizontally by 50%
  if (analysisType === SessionAnalysisType.TRAVERSE_TYRE_REFERENCE) {
    return (
      <span style={{ display: 'inline-block', transform: 'scaleX(1.5)' }} title={title}>
        <TraverseTyreReferenceIcon size={size} className={className} />
      </span>
    );
  }

  // Traverse Tyre Comparison - stretched horizontally by 50%
  if (analysisType === SessionAnalysisType.TRAVERSE_TYRE_COMPARISON) {
    return (
      <span style={{ display: 'inline-block', transform: 'scaleX(1.5)' }} title={title}>
        <TraverseTyreComparisonIcon size={size} className={className} />
      </span>
    );
  }

  // Traverse Indoors - stretched horizontally by 50%
  if (analysisType === SessionAnalysisType.TRAVERSE_INDOORS) {
    return (
      <span style={{ display: 'inline-block', transform: 'scaleX(1.5)' }} title={title}>
        <TraverseIndoorsIcon size={size} className={className} />
      </span>
    );
  }

  // Traverse Catesby - stretched horizontally by 50%
  if (analysisType === SessionAnalysisType.TRAVERSE_INDOORS_CONSTANT_GRADIENT) {
    return (
      <span style={{ display: 'inline-block', transform: 'scaleX(1.5)' }} title={title}>
        <TraverseCatesbyIcon size={size} className={className} />
      </span>
    );
  }

  // Basic Traverse or null - stretched horizontally by 50%
  if (analysisType === null || analysisType === SessionAnalysisType.TRAVERSE) {
    return (
      <span style={{ display: 'inline-block', transform: 'scaleX(1.5)' }} title={title}>
        <TraverseIcon size={size} className={className} />
      </span>
    );
  }

  // Velodrome Indoors - 40% bigger
  if (analysisType === SessionAnalysisType.LOOP_VELODROME_INDOORS) {
    const velodromeSize = size * 1.4;
    return (
      <span title={title}>
        <VelodromeIndoorIcon className={className} style={{ width: velodromeSize, height: velodromeSize }} />
      </span>
    );
  }

  // Velodrome Outdoors - 40% bigger
  if (analysisType === SessionAnalysisType.LOOP_VELODROME_OUTDOORS) {
    const velodromeSize = size * 1.4;
    return (
      <span title={title}>
        <VelodromeOutdoorIcon className={className} style={{ width: velodromeSize, height: velodromeSize }} />
      </span>
    );
  }

  // Freeride and Loop - use loop arrow
  if (
    analysisType === SessionAnalysisType.FREERIDE_CALIBRATED ||
    analysisType === SessionAnalysisType.FREERIDE_UNSTRUCTURED ||
    analysisType === SessionAnalysisType.LOOP
  ) {
    return (
      <span title={title}>
        <HiArrowPath size={size} className={className} />
      </span>
    );
  }

  // Default fallback
  return (
    <span title={title}>
      <TraverseIcon size={size} className={className} />
    </span>
  );
};
